<template>
  <!-- Filter stats -->
  <div class="flex flex-wrap items-center justify-end pb-6 md:pb-0">
    <div>
      <label class="block uppercase text-left text-xs font-bold mb-2">
        {{ $t("filter.range") }}
      </label>

      <div class="rounded border">
        <label
          v-for="option in rangeOptions"
          :key="option.value"
          class="inline-flex items-center cursor-pointer"
        >
          <button
            v-if="displayNavigator(option.value)"
            @click="navigateTo(option.value, -1)"
            class="text-white bg-emerald-500 font-bold uppercase text-xs px-4 py-2 shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            <i class="fa fa-arrow-left"></i>
          </button>
          <input
            type="radio"
            :value="option.value"
            v-model="range"
            class="hidden"
          />
          <span
            :style="
              option.color && range !== option.value
                ? `background-color: ${option.color}`
                : ''
            "
            :class="{
              'bg-emerald-500 text-white': range === option.value,
              'bg-white text-black': range !== option.value,
            }"
            class="text-xs font-bold uppercase px-4 py-2 shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            {{ $t(getLabel(option)) }}
          </span>
          <button
            v-if="displayNavigator(option.value)"
            @click="navigateTo(option.value, 1)"
            class="text-white bg-emerald-500 font-bold uppercase text-xs px-4 py-2 shadow hover:shadow-md outline-none focus:outline-none ease-linear transition-all duration-150"
          >
            <i class="fa fa-arrow-right"></i>
          </button>
        </label>
      </div>
    </div>
    <div class="ml-2" v-if="isRangeCustom">
      <label class="block uppercase text-left text-xs font-bold mb-2">
        {{ $t("filter.from") }}
      </label>
      <input
        type="date"
        v-model="startDate"
        class="border-0 px-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
      />
    </div>
    <div class="ml-2" v-if="isRangeCustom">
      <label class="block uppercase text-left text-xs font-bold mb-2">
        {{ $t("filter.to") }}
      </label>
      <input
        type="date"
        v-model="endDate"
        class="border-0 px-3 placeholder-blueGray-300 text-blueGray-600 bg-white rounded text-sm shadow focus:outline-none focus:ring w-full ease-linear transition-all duration-150"
      />
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import store from "@/store";
import DateUtils from "@/utils/dateUtils";

export default {
  data() {
    return {
      startDate: "",
      endDate: "",
      range: "",
      rangeOptions: [
        { value: "full", label: "filter.history", color: "#FFF3E0" },
        { value: "year", label: "filter.currentYear" },
        { value: "month", label: "filter.currentMonth" },
        { value: "custom", label: "filter.customRange" },
      ],
      monthNames: [
        "Enero",
        "Febrero",
        "Marzo",
        "Abril",
        "Mayo",
        "Junio",
        "Julio",
        "Agosto",
        "Setiembre",
        "Octubre",
        "Noviembre",
        "Diciembre",
      ],
    };
  },

  computed: {
    ...mapState({
      startDateFromState: (state) => state.filters.startDate,
      endDateFromState: (state) => state.filters.endDate,
    }),

    isRangeFull() {
      return this.startDate === null && this.endDate === null;
    },

    isRangeYear() {
      const range = this.getDateRanges("year");
      return (
        range.startDate === this.startDate && range.endDate === this.endDate
      );
    },

    isRangeMonth() {
      const range = this.getDateRanges("month");
      return (
        range.startDate === this.startDate && range.endDate === this.endDate
      );
    },

    isRangeCustom() {
      return this.range === "custom";
    },
  },

  watch: {
    $route: {
      immediate: true,
      handler() {
        ["startDateFromState", "endDateFromState"].forEach((property) => {
          this.$watch(
            () => this[property],
            () => {
              this.startDate = this.startDateFromState;
              this.endDate = this.endDateFromState;
            }
          );
        });
        ["startDate", "endDate", "range"].forEach((property) => {
          this.$watch(
            () => this[property],
            () => {
              this.dispatchFilter();
            }
          );
        });
      },
    },
    range: {
      immediate: true,
      handler() {
        const range = this.getDateRanges(this.range);
        this.startDate = range.startDate;
        this.endDate = range.endDate;
        this.dispatchFilter();
      },
    },
  },

  mounted: function () {
    if (this.startDateFromState === null) {
      // full/histórico mode was explicitly set
      this.startDate = null;
      this.endDate = null;
      this.range = "full";
    } else if (this.startDateFromState) {
      // a specific date range was stored
      this.startDate = this.startDateFromState;
      this.endDate = this.endDateFromState;
      if (this.isRangeYear) {
        this.range = "year";
      } else if (this.isRangeMonth) {
        this.range = "month";
      } else {
        this.range = "custom";
      }
    } else {
      // never set — default to current month
      const currentDate = new Date();
      this.startDate = DateUtils.formatDate(
        new Date(currentDate.getFullYear(), currentDate.getMonth(), 1)
      );
      this.endDate = DateUtils.formatDate(currentDate);
      this.dispatchFilter();
    }
  },

  methods: {
    dispatchFilter() {
      const startDate = this.startDate;
      const endDate = this.endDate;
      const filters = { startDate, endDate };

      store.dispatch("filters/update", filters);
    },

    getDateRanges(range) {
      const currentDate = new Date();

      let startDateObject = new Date(
        currentDate.getFullYear(),
        range === "year" ? 0 : currentDate.getMonth(),
        1
      );

      let endDateObject = new Date(
        currentDate.getFullYear(),
        range === "year" ? 12 : currentDate.getMonth() + 1,
        0
      );

      if (range === "full") {
        return { startDate: null, endDate: null };
      }

      const startDate = DateUtils.formatDate(startDateObject);
      const endDate = DateUtils.formatDate(endDateObject);

      return { startDate, endDate };
    },

    navigateTo(range, step) {
      let startDate = DateUtils.parseDate(this.startDate);
      let endDate = DateUtils.parseDate(this.endDate);

      switch (range) {
        case "full":
          startDate = DateUtils.parseDate("1-1-1");
          endDate = DateUtils.parseDate("9999-12-31");
          break;
        case "year":
          startDate.setFullYear(startDate.getFullYear() + step);
          endDate = new Date(startDate.getFullYear(), 12, 0);
          break;
        case "month":
          startDate.setMonth(startDate.getMonth() + step);
          endDate = new Date(
            startDate.getFullYear(),
            startDate.getMonth() + 1,
            0
          );
          break;
        default:
          startDate.setDate(startDate.getDate() + step);
          endDate.setDate(endDate.getDate() + step);
          break;
      }

      this.startDate = DateUtils.formatDate(startDate);
      this.endDate = DateUtils.formatDate(endDate);
    },

    displayNavigator(range) {
      return range !== "custom" && range !== "full" && range === this.range;
    },

    getLabel(option) {
      const { label, value } = option;
      const startDate = DateUtils.parseDate(this.startDate);
      const today = new Date();
      const currentYear =
        startDate && startDate.getFullYear() === today.getFullYear();
      const currentMonth =
        currentYear && startDate.getMonth() === today.getMonth();

      if (
        value !== "full" &&
        this.range === value &&
        !this.isRangeCustom &&
        (value !== "year" || !currentYear) &&
        !currentMonth
      ) {
        return value === "year"
          ? startDate.getFullYear()
          : `${this.monthNames[startDate.getMonth()].slice(
              0,
              3
            )} ${startDate.getFullYear()}`;
      }

      return label;
    },
  },
};
</script>
